import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import posthog from "posthog-js";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { ToastType } from "@/enums";

interface SocialCallbackParams {
  code: string;
  state: string;
  provider: string;
}

export const useSocialCallback = () => {
  const auth = useAuth();
  const navigate = useNavigate();

  return useMutation(
    ["social-callback"],
    async (params: SocialCallbackParams) =>
      fetch(ENDPOINTS.auth.social_callback, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          code: params.code,
          state: params.state,
          provider: params.provider,
        }),
      }).then(handleResponse),
    {
      onSuccess: ({ access_token, user, is_new_user }) => {
        auth?.login(access_token, user);
        if (is_new_user) {
          posthog.capture("Signed Up");
        }
        navigate(is_new_user ? "/onboarding" : "/");
      },
      onError: (err: {
        code: string;
        email: string;
        pending_authentication_token: string;
        user_id: string;
        provider: string;
        error_code: string;
        state: string;
      }) => {
        const { code, email, pending_authentication_token, user_id, provider, error_code, state } =
          err;
        const isVerificationRequired = code === "email_verification_required";

        if (error_code === "sso_required") {
          navigate(`/login?email=${encodeURIComponent(email)}&sso=true`);
          displayErrorToast("This is an SSO account. Please log in with your email to continue.");
          return;
        }

        let callbackUrl = "/verify-email";

        if (email) {
          callbackUrl += `?email=${encodeURIComponent(email)}`;
        }

        if (pending_authentication_token) {
          callbackUrl += `&pending_token=${encodeURIComponent(pending_authentication_token)}`;
        }
        if (provider) {
          callbackUrl += `&provider=${provider}`;
        }
        if (user_id) {
          callbackUrl += `&user_id=${encodeURIComponent(user_id)}`;
        }
        if (state) {
          callbackUrl += `&state=${encodeURIComponent(state)}`;
        }

        if (isVerificationRequired) {
          navigate(callbackUrl);
          displayToast("Please verify your email.", ToastType.warning);
        }
      },
    },
  );
};
